import React, { useState, memo, useEffect, useMemo } from "react";
import { Button, Flex, Input, Tree, theme } from "antd";
import FolderService from "services/folder";
import * as Icon from "@ant-design/icons";
import _ from "lodash";
import { useItemsScreenContext } from "pages/items";
import { useAppContext } from "context/app";
import { useLoading } from "context/loading";
import "./FolderTree.css";
import { findParentKeys, toTreeData } from "common/folder_common";
const folderService = new FolderService();

const { useToken } = theme;

const FolderTree = ({ value, onChange, onPathChange }) => {
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [searchValue, setSearchValue] = useState("");
    const [showSortIndexFolder, setShowSortIndexFolder] = useState(false);

    const { token } = useToken();
    const folders = useAppContext().folderList;
    const treeData = toTreeData([...folders]);
    const { currentFolder, setCurrentFolder, currentFolderPath, setCurrentFolderPath } = useItemsScreenContext();
    const { showLoading, dismissLoading } = useLoading();

    useEffect(() => {
        //loadTreeData();
    }, [folders]);

    useEffect(() => {
        const parentKeys = findParentKeys(treeData, currentFolder);
        setExpandedKeys([...parentKeys, currentFolder]);
        setSelectedKeys([currentFolder]);
    }, [folders, currentFolder]);

    const onSelect = (key) => {
        if (key && key.length > 0) {
            setCurrentFolder(key[0]);
        }
    };

    const onExpand = (keys) => {
        setExpandedKeys(keys);
    };

    const getParentKey = (key, tree) => {
        let parentKey;
        for (let i = 0; i < tree.length; i++) {
            const node = tree[i];
            if (node.children) {
                if (node.children.some((item) => item.key === key)) {
                    parentKey = node.key;
                } else if (getParentKey(key, node.children)) {
                    parentKey = getParentKey(key, node.children);
                }
            }
        }
        return parentKey;
    };

    const handleSearch = (e) => {
        const { value } = e.target;
        const newExpandedKeys = folders
            .map((item) => {
                if (item.name && item.name.toLowerCase().indexOf(value.toLowerCase()) > -1) {
                    return findParentKeys(treeData, item.id);
                }
                return [];
            })
            .flat()
            .filter((key, index, self) => self.indexOf(key) === index);

        setExpandedKeys(newExpandedKeys);
        setSearchValue(value);
    };

    const data = useMemo(() => {
        if (searchValue === "") {
            var tree = treeData;
            const parentKeys = findParentKeys(tree, currentFolder);
            setExpandedKeys([...parentKeys, currentFolder]);
            setSelectedKeys([currentFolder]);
            return tree;
        }

        const normalizeString = (str) => str.toLowerCase();
        const loop = (data) =>
            data.map((item) => {
                const strTitle = item.title;
                const normalizedTitle = normalizeString(strTitle);
                const normalizedSearchValue = normalizeString(searchValue);

                const index = normalizedTitle.indexOf(normalizedSearchValue);
                const beforeStr = strTitle.substring(0, index);
                const highlightedStr = strTitle.substring(index, index + searchValue.length);
                const afterStr = strTitle.slice(index + searchValue.length);
                const title =
                    index > -1 ? (
                        <span key={item.key}>
                            {beforeStr}
                            <span style={{ color: token.colorPrimary }}>{highlightedStr}</span>
                            {afterStr}
                        </span>
                    ) : (
                        <span key={item.key}>{strTitle}</span>
                    );

                if (item.children) {
                    return {
                        id: item.id,
                        value: item.id,
                        title,
                        key: item.key,
                        children: loop(item.children),
                    };
                }

                return {
                    title,
                    key: item.key,
                    id: item.id,
                    value: item.id,
                };
            });

        return loop(treeData);
    }, [searchValue, folders]);

    const onDrop = async (info) => {
        const dragNodeKey = info.dragNode.key; // Key thư mục đang được kéo thả
        const dropNodeKey = info.node.key; // Key thư mục trên chỗ thư mục đã được thả vào

        // Hàm lấy danh sách id của các thư mục cùng cấp
        const getSiblings = (key, data) => {
            let siblings = [];
            const findSiblings = (nodes) => {
                for (let node of nodes) {
                    if (node.children) {
                        if (node.children.some((child) => child.key === key)) {
                            siblings = node.children.map((child) => child.key);
                            return;
                        }
                        findSiblings(node.children);
                    }
                }
            };
            findSiblings(data);
            return siblings;
        };

        // Cập nhật danh sách sort index cần thay đổi
        const updateSortIndex = async (siblings, originalSiblings) => {
            const updatedSortIndexFolders = siblings.map((id, index) => ({ id, sort_index: index })).filter((item, index) => originalSiblings[index] !== siblings[index]);

            if (updatedSortIndexFolders.length > 0) {
                try {
                    showLoading();
                    await folderService.bulkUpdateSortIndex(updatedSortIndexFolders);
                    setCurrentFolder(dragNodeKey);
                } catch (err) {
                    console.error(err);
                } finally {
                    dismissLoading();
                }
            }
        };

        // Xác định các thư mục cùng cấp của thư mục thả
        const siblings = getSiblings(dropNodeKey, treeData);
        const siblingsOriginal = [...siblings];

        // Kiểm tra nếu thư mục kéo và thư mục thả đều là các thư mục cùng cấp
        if (siblings.includes(dragNodeKey)) {
            const dragIndex = siblings.indexOf(dragNodeKey);
            const dropIndex = siblings.indexOf(dropNodeKey);

            siblings.splice(dragIndex, 1);
            const newIndex = dragIndex < dropIndex ? dropIndex : dropIndex + 1;
            siblings.splice(newIndex, 0, dragNodeKey);

            await updateSortIndex(siblings, siblingsOriginal);
        } else if (!info.dropToGap) {
            const lstChildrenDrop = info.node.children;
            const checkDragNodeExists = lstChildrenDrop.some((node) => node.key === dragNodeKey);

            if (checkDragNodeExists) {
                const siblings = getSiblings(dragNodeKey, treeData);
                const siblingsOriginal = [...siblings];

                siblings.splice(siblings.indexOf(dragNodeKey), 1);
                siblings.unshift(dragNodeKey);

                await updateSortIndex(siblings, siblingsOriginal);
            }
        }
    };

    return (
        <div className="custom">
            <div>
                <Input style={{ margin: "10px 0 12px" }} placeholder="Search" addonBefore={<Icon.SearchOutlined />} onChange={handleSearch} />
            </div>
            <div style={{ backgroundColor: token.colorBgContainer }}>
                <Tree
                    style={{
                        width: "100%",
                    }}
                    value={value}
                    height={880}
                    suffixIcon={<Icon.DownOutlined size={64} />}
                    treeData={data}
                    placeholder="Please select"
                    showLine={true}
                    defaultExpandAll={true}
                    expandedKeys={expandedKeys}
                    selectedKeys={selectedKeys}
                    onExpand={onExpand}
                    onSelect={onSelect}
                    draggable={showSortIndexFolder}
                    blockNode
                    onDrop={onDrop}
                    titleRender={(node) => (
                        <Flex justify="space-between">
                            {node.title}
                            {node.isRoot ? (
                                <Button
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowSortIndexFolder((prev) => !prev);
                                    }}
                                >
                                    {showSortIndexFolder ? <Icon.CloseOutlined /> : <Icon.HolderOutlined />}
                                </Button>
                            ) : (
                                showSortIndexFolder && <Icon.HolderOutlined style={{ marginRight: "8px" }} />
                            )}
                        </Flex>
                    )}
                />
            </div>
        </div>
    );
};
export default memo(FolderTree);
